/**
 * Slippage Tolerance Dialog
 * Allows users to adjust their maximum slippage tolerance for market orders
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Lock } from "lucide-react";
import { useTradingSettings } from "./trading-settings-context";
import { cn, formatCurrency } from "@/lib/utils";
import { 
  TOLERANCE_PRESETS, 
  formatTolerancePercent,
  getSuggestedTolerance 
} from "@/features/order-form/slippage-config";

interface SlippageToleranceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentOrderSize?: number;
  currentPrice?: number;
  suggestedTolerance?: number;
}

export const SlippageToleranceDialog = ({
  open,
  onOpenChange,
  currentOrderSize = 0,
  currentPrice = 0,
  suggestedTolerance,
}: SlippageToleranceDialogProps) => {
  const { slippageTolerance, setSlippageTolerance } = useTradingSettings();
  
  const [selectedTolerance, setSelectedTolerance] = useState(slippageTolerance);
  const [customValue, setCustomValue] = useState("");
  const [isCustom, setIsCustom] = useState(false);

  // Calculate order size in USD
  const orderSizeUsd = currentOrderSize * currentPrice;
  
  // Get minimum required tolerance for this order
  const minimumRequired = suggestedTolerance || getSuggestedTolerance(orderSizeUsd);

  // Reset to current tolerance when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedTolerance(slippageTolerance);
      const isPreset = slippageTolerance === null || (TOLERANCE_PRESETS as readonly number[]).includes(slippageTolerance);
      setIsCustom(!isPreset);
      if (!isPreset && slippageTolerance !== null) {
        setCustomValue((slippageTolerance * 100).toFixed(2));
      } else {
        setCustomValue("");
      }
    }
  }, [open, slippageTolerance]);

  const handlePresetClick = (preset: number) => {
    setSelectedTolerance(preset);
    setIsCustom(false);
    setCustomValue("");
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomValue(value);
    setIsCustom(true);
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setSelectedTolerance(numValue / 100); // Convert % to decimal
    }
  };

  const handleSave = () => {
    setSlippageTolerance(selectedTolerance);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Max Slippage Tolerance</DialogTitle>
          <DialogDescription>
            Maximum price difference you'll accept for market orders.
            {orderSizeUsd > 0 && (
              <span className="block mt-1 text-foreground">
                For this ${formatCurrency(orderSizeUsd, 0)} order, we recommend at least{" "}
                <span className="font-semibold">{formatTolerancePercent(minimumRequired)}</span>.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Preset buttons */}
          <div>
            <Label className="text-sm mb-2 block">Quick Presets</Label>
            <div className="grid grid-cols-4 gap-2">
              {TOLERANCE_PRESETS.map((preset) => {
                const isTooLow = preset < minimumRequired;
                const isSelected = !isCustom && selectedTolerance === preset;
                
                return (
                  <Button
                    key={preset}
                    variant={isSelected ? "default" : "outline"}
                    onClick={() => handlePresetClick(preset)}
                    disabled={isTooLow}
                    className={cn(
                      "relative",
                      isTooLow && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {formatTolerancePercent(preset)}
                    {isTooLow && (
                      <Lock className="ml-1 h-3 w-3" />
                    )}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Custom input */}
          <div>
            <Label htmlFor="custom-tolerance" className="text-sm mb-2 block">
              Custom Value (%)
            </Label>
            <Input
              id="custom-tolerance"
              type="number"
              step="0.1"
              min="0.01"
              max="10"
              value={customValue}
              onChange={handleCustomChange}
              placeholder="Enter custom percentage"
              className={cn(
                isCustom && "ring-2 ring-primary"
              )}
            />
          </div>

          {/* Warning if below minimum */}
          {selectedTolerance !== null && selectedTolerance < minimumRequired && (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">
                This tolerance is too low for your current order size. 
                The order will be rejected. Please select at least {formatTolerancePercent(minimumRequired)}.
              </AlertDescription>
            </Alert>
          )}

          {/* Info box */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Lower values protect you from unexpected price changes, but large orders
              may require higher tolerance to execute successfully.
            </AlertDescription>
          </Alert>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={selectedTolerance !== null && selectedTolerance < minimumRequired}
              className="flex-1"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
